import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { 
  Camera, 
  Droplets, 
  Settings, 
  RefreshCw, 
  Maximize2,
  Wifi,
  WifiOff,
  AlertTriangle,
  CheckCircle2,
  Activity,
  ExternalLink,
  Save,
  Loader2,
  LogOut,
  Users,
  Lock,
  Unlock,
  BarChart3,
  Clock
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { toast } from "sonner";
import QuickUpdate from "@/components/QuickUpdate";
import { classifyIncident } from "@/lib/incident";
import LevelsChart from "@/components/LevelsChart";
import WeatherWidget from "@/components/WeatherWidget";
import AlertsPanel from "@/components/AlertsPanel";
import NotificationSettings from "@/components/NotificationSettings";
import ReportGenerator from "@/components/ReportGenerator";
import EpisodeSimulator from "@/components/EpisodeSimulator";
import IrrigationManager from "@/components/IrrigationManager";
import MapView from "@/components/MapView";
import EventLog from "@/components/EventLog";
import SeismicAlert from "@/components/SeismicAlert";
import WindAlert from "@/components/WindAlert";
import AlertsStatsPanel from "@/components/AlertsStatsPanel";
import ConnectionIndicator from "@/components/ConnectionIndicator";
import PushNotificationToggle from "@/components/PushNotificationToggle";
import OfflineIndicator from "@/components/OfflineIndicator";
import InstallPWA from "@/components/InstallPWA";
import AdvancedStatistics from "@/components/AdvancedStatistics";
import DataExport from "@/components/DataExport";
import PromoBanner from "@/components/PromoBanner";
import IrrigationStats, { IrrigationWidget, IrrigationStatsButton } from "@/components/IrrigationStats";
import SeismicHistory, { SeismicHistoryButton } from "@/components/SeismicHistory";
import DailyReport, { DailyReportButton } from "@/components/DailyReport";
import VisorView from "@/components/VisorView";
import useOfflineSync from "@/hooks/useOfflineSync";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Camera component - Snapshot inicial + HLS video
const LiveStream = ({ cameraKey, quality = "low", className = "", delayStart = 0 }) => {
  const [started, setStarted] = useState(false);
  const [hlsReady, setHlsReady] = useState(false);
  const [hasError, setHasError] = useState(false);
  const videoRef = useRef(null);
  const hlsRef = useRef(null);
  
  // Snapshot para carga inmediata
  const snapshotUrl = `${API}/cameras/snapshot-live/${cameraKey}?t=${Date.now()}`;
  
  // HLS URL desde MediaMTX
  const hlsUrl = `${API}/cameras/hls/${cameraKey}/index.m3u8`;
  
  // URL RTSP para VLC
  const rtspUrls = {
    'parralillo': 'rtsp://admin:livewifi7500@82.223.48.250:1029/cam/realmonitor?channel=1&subtype=0',
    'siberio1': 'rtsp://admin:livewifi7500@82.223.48.250:1027/cam/realmonitor?channel=1&subtype=0',
    'siberio2': 'rtsp://admin:livewifi7500@82.223.48.250:1028/cam/realmonitor?channel=1&subtype=0',
    'caidero1': 'rtsp://admin:livewifi7500@82.223.48.250:1025/cam/realmonitor?channel=1&subtype=0',
    'caidero2': 'rtsp://admin:livewifi7500@82.223.48.250:1026/cam/realmonitor?channel=1&subtype=0',
  };
  
  useEffect(() => {
    const timer = setTimeout(() => setStarted(true), delayStart);
    return () => {
      clearTimeout(timer);
      if (hlsRef.current) hlsRef.current.destroy();
    };
  }, [delayStart]);
  
  useEffect(() => {
    if (!started || !videoRef.current) return;
    
    const initHLS = async () => {
      try {
        const Hls = (await import('hls.js')).default;
        
        if (Hls.isSupported()) {
          const hls = new Hls({
            enableWorker: true,
            backBufferLength: 0,
            maxBufferLength: 3,
          });
          
          hlsRef.current = hls;
          hls.loadSource(hlsUrl);
          hls.attachMedia(videoRef.current);
          
          hls.on(Hls.Events.MANIFEST_PARSED, () => {
            setHlsReady(true);
            videoRef.current?.play().catch(() => {});
          });
          
          hls.on(Hls.Events.ERROR, (_, data) => {
            if (data.fatal) setHasError(true);
          });
        }
      } catch (e) {
        setHasError(true);
      }
    };
    
    initHLS();
    
    return () => {
      if (hlsRef.current) {
        hlsRef.current.destroy();
        hlsRef.current = null;
      }
    };
  }, [started, hlsUrl]);
  
  const openInPlayer = () => {
    const rtspUrl = rtspUrls[cameraKey];
    if (rtspUrl) {
      navigator.clipboard.writeText(rtspUrl);
      alert(`URL RTSP:\n\n${rtspUrl}\n\nÁbrela en VLC.`);
    }
  };
  
  return (
    <div className={`relative w-full h-full bg-obsidian-surface border border-white/5 overflow-hidden ${className}`}>
      {/* Snapshot - Carga inmediata */}
      {started && !hlsReady && (
        <img
          src={snapshotUrl}
          alt={`Camera ${cameraKey}`}
          className="absolute inset-0 w-full h-full object-cover"
        />
      )}
      
      {/* HLS Video - Cuando esté listo */}
      {started && (
        <video
          ref={videoRef}
          autoPlay
          muted
          playsInline
          className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-300 ${hlsReady ? 'opacity-100' : 'opacity-0'}`}
        />
      )}
      
      {!started && (
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="w-8 h-8 border-2 border-green-500 border-t-transparent rounded-full animate-spin" />
        </div>
      )}
      
      {started && (
        <>
          <div className="absolute top-1 left-1 flex items-center gap-1 bg-black/60 px-1.5 py-0.5 rounded">
            <div className={`w-1.5 h-1.5 rounded-full animate-pulse ${hlsReady ? 'bg-green-500' : 'bg-yellow-500'}`} />
            <span className={`text-[7px] font-mono ${hlsReady ? 'text-green-400' : 'text-yellow-400'}`}>
              {hlsReady ? 'EN VIVO' : 'CARGANDO'}
            </span>
          </div>
          <button
            onClick={openInPlayer}
            className="absolute bottom-1 right-1 bg-blue-600/80 hover:bg-blue-500 px-1.5 py-0.5 rounded text-[7px] text-white font-mono"
          >
            HD
          </button>
        </>
      )}
    </div>
  );
};

// Fullscreen view component - HLS Stream en ALTA CALIDAD
const FullscreenVideoStream = ({ cameraKey, className = "" }) => {
  const [currentTime, setCurrentTime] = useState(new Date());
  const [hasError, setHasError] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const videoRef = useRef(null);
  const hlsRef = useRef(null);
  
  // HLS URL desde MediaMTX (ya es H.264 alta calidad)
  const hlsUrl = `${API}/cameras/hls/${cameraKey}/index.m3u8`;
  
  // MJPEG fallback en ALTA CALIDAD
  const mjpegUrl = `${API}/cameras/stream/${cameraKey}?quality=high&t=${Date.now()}`;
  
  // URLs RTSP para VLC
  const rtspUrls = {
    'parralillo': 'rtsp://admin:livewifi7500@82.223.48.250:1029/cam/realmonitor?channel=1&subtype=0',
    'siberio1': 'rtsp://admin:livewifi7500@82.223.48.250:1027/cam/realmonitor?channel=1&subtype=0',
    'siberio2': 'rtsp://admin:livewifi7500@82.223.48.250:1028/cam/realmonitor?channel=1&subtype=0',
    'caidero1': 'rtsp://admin:livewifi7500@82.223.48.250:1025/cam/realmonitor?channel=1&subtype=0',
    'caidero2': 'rtsp://admin:livewifi7500@82.223.48.250:1026/cam/realmonitor?channel=1&subtype=0',
  };
  
  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => {
      clearInterval(timer);
      if (hlsRef.current) {
        hlsRef.current.destroy();
      }
    };
  }, []);
  
  useEffect(() => {
    if (!videoRef.current) return;
    
    const initHLS = async () => {
      try {
        const Hls = (await import('hls.js')).default;
        
        if (Hls.isSupported()) {
          const hls = new Hls({
            enableWorker: true,
            lowLatencyMode: false,
            backBufferLength: 0,
            maxBufferLength: 5,
            liveSyncDurationCount: 2,
          });
          
          hlsRef.current = hls;
          hls.loadSource(hlsUrl);
          hls.attachMedia(videoRef.current);
          
          hls.on(Hls.Events.MANIFEST_PARSED, () => {
            setIsLoading(false);
            setHasError(false);
            videoRef.current?.play().catch(() => {});
          });
          
          hls.on(Hls.Events.ERROR, (_, data) => {
            if (data.fatal) {
              setHasError(true);
              setIsLoading(false);
            }
          });
        } else if (videoRef.current.canPlayType('application/vnd.apple.mpegurl')) {
          videoRef.current.src = hlsUrl;
          videoRef.current.addEventListener('canplay', () => {
            setIsLoading(false);
            videoRef.current?.play().catch(() => {});
          });
        }
      } catch (e) {
        setHasError(true);
        setIsLoading(false);
      }
    };
    
    initHLS();
    
    return () => {
      if (hlsRef.current) {
        hlsRef.current.destroy();
        hlsRef.current = null;
      }
    };
  }, [hlsUrl]);
  
  const openInPlayer = () => {
    const rtspUrl = rtspUrls[cameraKey];
    if (rtspUrl) {
      navigator.clipboard.writeText(rtspUrl);
      alert(`URL RTSP H264 copiada:\n\n${rtspUrl}\n\nÁbrela en VLC Media Player.`);
    }
  };
  
  return (
    <div className={`relative w-full h-full bg-black overflow-hidden ${className}`}>
      {/* HLS Video */}
      {!hasError && (
        <video
          ref={videoRef}
          autoPlay
          muted
          playsInline
          className="w-full h-full object-contain"
        />
      )}
      
      {/* MJPEG Fallback */}
      {hasError && (
        <img
          src={mjpegUrl}
          alt="Live Video"
          className="w-full h-full object-contain"
        />
      )}
      
      {isLoading && (
        <div className="absolute inset-0 flex flex-col items-center justify-center bg-black">
          <div className="w-12 h-12 border-3 border-green-500 border-t-transparent rounded-full animate-spin" />
          <span className="text-sm text-zinc-400 mt-3">Cargando stream...</span>
        </div>
      )}
      
      {!isLoading && (
        <div className="absolute top-3 left-3 flex items-center gap-2">
          <div className="bg-green-600/80 px-2 py-1 rounded flex items-center gap-1.5 shadow-lg">
            <span className="w-2 h-2 bg-green-300 rounded-full animate-pulse" />
            <span className="text-xs text-white font-semibold">EN VIVO</span>
          </div>
          <div className="bg-black/60 backdrop-blur-sm px-2 py-1 rounded">
            <span className="text-xs text-white font-mono">
              {currentTime.toLocaleTimeString('es-ES', { 
                hour: '2-digit', 
                minute: '2-digit', 
                second: '2-digit',
                timeZone: 'Atlantic/Canary' 
              })}
            </span>
          </div>
          <button
            onClick={openInPlayer}
            className="bg-blue-600 hover:bg-blue-500 px-3 py-1 rounded text-xs text-white font-semibold"
          >
            Abrir en VLC (HD)
          </button>
        </div>
      )}
    </div>
  );
};

const CameraFeed = ({ camera, onFullscreen, delayIndex = 0 }) => {
  const hasUrl = camera.url && camera.url.trim() !== "";
  const isWeincloud = camera.url?.includes('weincloud');
  
  // Extract camera key
  const getCameraKey = () => {
    const name = camera.name.toLowerCase().replace(' ', '');
    const keyMap = {
      'parralillo': 'parralillo',
      'siberio1': 'siberio1',
      'siberio2': 'siberio2',
      'caidero1': 'caidero1',
      'caidero2': 'caidero2',
      'caideros1': 'caidero1',
      'caideros2': 'caidero2'
    };
    return keyMap[name] || name;
  };

  const openWeincloud = () => {
    window.open('https://www.weincloud.net/webview', '_blank');
  };

  return (
    <div 
      className="camera-feed group relative h-full w-full min-h-0 overflow-hidden cursor-pointer"
      data-testid={`camera-feed-${camera.id}`}
      style={{ maxHeight: '100%' }}
      onDoubleClick={() => !isWeincloud && onFullscreen && onFullscreen(camera)}
    >
      {isWeincloud ? (
        <div 
          className="camera-placeholder cursor-pointer hover:bg-zinc-800/50 transition-colors" 
          onClick={openWeincloud}
        >
          <Camera className="w-14 h-14 mb-2 text-blue-500" />
          <span className="text-lg font-mono text-zinc-300 font-bold">{camera.name}</span>
          <span className="text-xs mt-2 px-3 py-1.5 bg-blue-600 hover:bg-blue-500 rounded text-white font-mono transition-colors">
            CLICK PARA VER EN WEINCLOUD
          </span>
        </div>
      ) : (
        <LiveStream cameraKey={getCameraKey()} delayStart={delayIndex * 400} />
      )}
      
      <div className="camera-overlay" />
      
      <div className="camera-info">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-heading font-bold text-white uppercase tracking-wider text-sm">
              {camera.name}
            </h3>
            <p className="text-xs text-white/60 font-mono">{camera.location}</p>
          </div>
          <div className="flex items-center gap-2">
            {isWeincloud && (
              <div className="live-indicator bg-blue-600/80">
                <span className="text-white/90">WEBVIEW</span>
              </div>
            )}
            {!isWeincloud && (
              <div className="live-indicator">
                <div className="live-dot online" />
                <span className="text-white/80">LIVE</span>
              </div>
            )}
          </div>
        </div>
      </div>
      
      {/* Hover actions */}
      {!isWeincloud && (
        <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
          <Button 
            size="icon" 
            variant="ghost" 
            className="h-8 w-8 bg-black/50 hover:bg-black/70 text-white"
            onClick={() => onFullscreen(camera)}
            data-testid={`fullscreen-btn-${camera.id}`}
          >
            <Maximize2 className="w-4 h-4" />
          </Button>
        </div>
      )}
    </div>
  );
};

const WaterGauge = ({ sensor, onUpdate, onClick, isFullscreen = false, user, consumption }) => {
  const isOffline = sensor.connection_status === 'offline';
  const incident = isOffline ? classifyIncident(sensor) : null;
  // Mostrar nivel con 2 decimales
  const formatLevel = (val) => {
    if (val === null || val === undefined) return '--';
    return Number(val).toFixed(2);
  };
  const level = sensor.unit === 'm' ? formatLevel(sensor.current_level) : Math.round(sensor.current_level || 0);
  
  // DATOS REALES CONSOLIDADOS - Capacidades verificadas
  const DAM_DATA = {
    'PARRALILLO': { 
      maximo_m: 56.70, maximo_m3: 5576719,
      rebose_m: 53.00, rebose_m3: 4569808,
      minimo_m: 20.56, minimo_m3: 124427
    },
    'SIBERIO': { 
      maximo_m: 74.45, maximo_m3: 4500000, // Estimado
      rebose_m: 74.00, rebose_m3: 4356364,
      minimo_m: 17.00, minimo_m3: 69000
    },
    'CAIDERO': { 
      maximo_m: 46.60, maximo_m3: 3106392,
      rebose_m: 40.00, rebose_m3: 1997040,
      minimo_m: 14.17, minimo_m3: 1157
    }
  };
  
  const WATER_HOUR_M3 = 36; // 1 hora de agua = 36 m³
  const damData = DAM_DATA[sensor.name] || { 
    maximo_m: 100, maximo_m3: 1000000,
    rebose_m: 80, rebose_m3: 800000,
    minimo_m: 10, minimo_m3: 1000
  };
  
  // Calcular volumen actual con interpolación real
  const calculateVolume = (nivel) => {
    const { minimo_m, minimo_m3, rebose_m, rebose_m3, maximo_m, maximo_m3 } = damData;
    
    if (nivel <= minimo_m) return minimo_m3;
    
    if (nivel <= rebose_m) {
      // Interpolación cuadrática entre mínimo y rebose (curva realista de embalse)
      const ratio = (nivel - minimo_m) / (rebose_m - minimo_m);
      const ratioVol = Math.pow(ratio, 1.5);
      return minimo_m3 + ratioVol * (rebose_m3 - minimo_m3);
    }
    
    // Por encima del rebose - interpolación hacia máximo
    const ratio = (nivel - rebose_m) / (maximo_m - rebose_m);
    return rebose_m3 + ratio * (maximo_m3 - rebose_m3);
  };
  
  const currentWaterVolume = Math.round(calculateVolume(sensor.current_level));
  const currentWaterHours = Math.round(currentWaterVolume / WATER_HOUR_M3);
  const currentWaterAzadas = (currentWaterVolume / WATER_HOUR_M3).toFixed(1); // 1 azada = 1 hora = 36 m³
  
  // Calcular porcentaje de llenado respecto al rebose
  const fillPercent = Math.min(110, (currentWaterVolume / damData.rebose_m3) * 100);
  const fillHeight = `${Math.min(100, fillPercent)}%`;
  
  // Formatear volumen para mostrar
  const formatVolume = (m3) => {
    if (m3 >= 1000000) return `${(m3 / 1000000).toFixed(2)} hm³`;
    if (m3 >= 1000) return `${Math.round(m3 / 1000).toLocaleString()} K m³`;
    return `${m3.toLocaleString()} m³`;
  };
  
  const formatVolumeM3 = (m3) => {
    return `${m3.toLocaleString('es-ES')} m³`;
  };
  
  const formatHours = (hours) => {
    if (hours >= 1000) return `${(hours / 1000).toFixed(1)}K h`;
    return `${hours.toLocaleString()} h`;
  };
  
  const formatAzadas = (azadas) => {
    if (azadas >= 1000) return `${(azadas / 1000).toFixed(1)}K az`;
    return `${parseFloat(azadas).toLocaleString('es-ES')} az`;
  };
  
  const [configOpen, setConfigOpen] = useState(false);
  const [configValues, setConfigValues] = useState({
    min_threshold: sensor.min_threshold,
    max_threshold: sensor.max_threshold,
    overflow_level: sensor.overflow_level || 90,
    modbus_ip: sensor.modbus_ip || '',
    modbus_port: sensor.modbus_port || 502,
    modbus_unit_id: sensor.modbus_unit_id || 247,
    modbus_line: sensor.modbus_register || sensor.modbus_line || '',
    modbus_bit: sensor.modbus_bit || '',
    level_offset: sensor.level_offset || 0,
    alert_rise: sensor.alert_rise || '',
    alert_drop: sensor.alert_drop || ''
  });
  const [saving, setSaving] = useState(false);
  const [showModbus, setShowModbus] = useState(false);
  const [testingModbus, setTestingModbus] = useState(false);
  const [modbusTestResult, setModbusTestResult] = useState(null);
  const [modbusLocked, setModbusLocked] = useState(true);
  const [unlockConfirmCount, setUnlockConfirmCount] = useState(0);
  const [calibrateValue, setCalibrateValue] = useState('');
  const [calibrating, setCalibrating] = useState(false);
  const [calibrationHistory, setCalibrationHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);

  const fetchCalibrationHistory = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/sensors/${sensor.id}/calibration-history`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCalibrationHistory(res.data || []);
    } catch (e) {
      setCalibrationHistory([]);
    }
  };

  useEffect(() => {
    if (configOpen) fetchCalibrationHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [configOpen]);

  const handleCalibrate = async () => {
    const real = parseFloat(String(calibrateValue).replace(',', '.'));
    if (isNaN(real)) {
      toast.error("Introduce una medida válida");
      return;
    }
    setCalibrating(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/sensors/${sensor.id}/calibrate`, { real_level: real }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(response.data.message || "Ajuste aplicado");
      setCalibrateValue('');
      if (onUpdate) onUpdate();
      fetchCalibrationHistory();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al ajustar la medida");
    } finally {
      setCalibrating(false);
    }
  };

  // Helper: Format number with comma for display
  const formatWithComma = (val) => {
    if (val === null || val === undefined || val === '') return '';
    return String(val).replace('.', ',');
  };

  // Helper: Handle decimal input (converts . to , and validates)
  const handleDecimalInput = (field) => (e) => {
    let val = e.target.value.replace('.', ',');
    val = val.replace(/[^0-9,-]/g, '');
    const parts = val.split(',');
    if (parts.length > 2) {
      val = parts[0] + ',' + parts.slice(1).join('');
    }
    setConfigValues(prev => ({ ...prev, [field]: val }));
  };

  const handleTestModbus = async () => {
    if (!configValues.modbus_ip) {
      toast.error("Introduce una IP para probar");
      return;
    }
    setTestingModbus(true);
    setModbusTestResult(null);
    try {
      const response = await axios.post(`${API}/sensors/test-modbus`, {
        ip: configValues.modbus_ip,
        port: parseInt(configValues.modbus_port) || 247
      });
      setModbusTestResult(response.data);
      if (response.data.success) {
        toast.success("Conexión exitosa");
      } else {
        toast.error(response.data.message || "Conexión fallida");
      }
    } catch (error) {
      setModbusTestResult({ success: false, message: "Error de red" });
      toast.error("Error al probar conexión");
    } finally {
      setTestingModbus(false);
    }
  };

  const handleSaveConfig = async () => {
    setSaving(true);
    try {
      // Helper function to parse numbers with comma or period
      const parseNumber = (val) => {
        if (val === null || val === undefined || val === '') return null;
        return parseFloat(String(val).replace(',', '.'));
      };
      
      await axios.put(`${API}/sensors/${sensor.id}`, {
        min_threshold: parseNumber(configValues.min_threshold),
        max_threshold: parseNumber(configValues.max_threshold),
        overflow_level: parseNumber(configValues.overflow_level),
        modbus_ip: configValues.modbus_ip || null,
        modbus_port: configValues.modbus_port ? parseInt(configValues.modbus_port) : 502,
        modbus_unit_id: configValues.modbus_unit_id ? parseInt(configValues.modbus_unit_id) : 247,
        modbus_register: configValues.modbus_line ? parseInt(configValues.modbus_line) : null,
        modbus_line: configValues.modbus_line ? parseInt(configValues.modbus_line) : null,
        modbus_bit: configValues.modbus_bit ? parseInt(configValues.modbus_bit) : null,
        level_offset: parseNumber(configValues.level_offset) || 0,
        alert_rise: parseNumber(configValues.alert_rise),
        alert_drop: parseNumber(configValues.alert_drop),
        reference_level: sensor.current_level // Guardar nivel actual como referencia
      });
      toast.success(`${sensor.name} configurado`);
      setConfigOpen(false);
      if (onUpdate) onUpdate();
    } catch (error) {
      toast.error("Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  // Handle double click - maximize all sensors
  const handleCardDoubleClick = (e) => {
    // Don't trigger if clicking on the config icon
    if (e.target.closest('[data-config-icon]')) return;
    if (onClick) {
      onClick();
    }
  };

  return (
    <>
      <Card 
        className={`bg-[#121212] border border-[#262626] h-full flex flex-col p-1 cursor-pointer hover:border-[#3B82F6]/50 transition-all duration-150 rounded-none ${isFullscreen ? 'p-3' : ''}`} 
        data-testid={`sensor-card-${sensor.id}`}
        onDoubleClick={handleCardDoubleClick}
      >
        {/* Header - Industrial Style - Responsive */}
        <div className={`flex items-center flex-wrap ${isFullscreen ? 'mb-2 px-2 pt-1' : 'mb-1 px-1'}`}>
          <span className={`font-industrial uppercase tracking-[0.1em] text-[#F3F4F6] font-bold ${isFullscreen ? 'text-lg' : 'text-[10px] md:text-base'}`}>
            {sensor.name}
          </span>
          {/* Config icon - next to name (only for users with can_configure_sensors) - Hidden on mobile */}
          {user?.permissions?.can_configure_sensors && (
            <button 
              data-config-icon
              onClick={(e) => { e.stopPropagation(); setConfigOpen(true); }}
              className={`text-zinc-500 hover:text-zinc-300 transition-colors ml-1 ${isFullscreen ? 'p-1' : 'p-0.5'}`}
              title="Configurar sonda"
              data-testid={`sensor-config-btn-${sensor.id}`}
            >
              <Settings className={isFullscreen ? 'w-5 h-5' : 'w-4 h-4'} />
            </button>
          )}
          
          {/* Current water volume - MOVED TO LEFT - Hidden on mobile */}
          <div className={`hidden md:flex items-center gap-1 ml-2 px-2 py-0.5 bg-[#1E1E1E] border border-[#262626] rounded-none ${isFullscreen ? 'text-sm' : 'text-[10px]'}`}>
            <span className="text-[#FACC15] font-industrial-mono font-bold" title="Horas de agua disponibles">
              {formatHours(currentWaterHours)}
            </span>
            <span className="text-[#404040] mx-0.5">|</span>
            <span className="text-[#10B981] font-industrial-mono font-bold" title="Azadas de agua">
              {formatAzadas(currentWaterAzadas)}
            </span>
            <span className="text-[#404040] mx-0.5">|</span>
            <span className="text-[#3B82F6] font-industrial-mono font-bold" title="Volumen en metros cúbicos">
              {formatVolumeM3(currentWaterVolume)}
            </span>
          </div>
          
          {/* Status badge - Industrial Style - Smaller on mobile - CLOSER TO LEFT */}
          <span 
            className={`inline-flex items-center border px-1 md:px-2 py-0.5 font-industrial-mono font-semibold ml-1.5 rounded-none ${
              sensor.status === 'normal' ? 'bg-[#10B981]/10 text-[#10B981] border-[#10B981]/50' :
              sensor.status === 'warning' ? 'bg-[#F59E0B]/10 text-[#F59E0B] border-[#F59E0B]/50' :
              sensor.status === 'overflow' ? 'bg-[#EF4444]/10 text-[#EF4444] border-[#EF4444]/50 status-critical' :
              sensor.status === 'low' ? 'bg-[#3B82F6]/10 text-[#3B82F6] border-[#3B82F6]/50' :
              'bg-[#6B7280]/10 text-[#6B7280] border-[#6B7280]/50'
            } ${isFullscreen ? 'text-sm' : 'text-[8px] md:text-xs'}`}
          >
            {sensor.status === 'normal' ? 'OK' : sensor.status === 'overflow' ? 'DESBORDE' : sensor.status === 'warning' ? 'ALERTA' : sensor.status === 'low' ? 'BAJO' : sensor.status.toUpperCase()}
          </span>
        </div>
        
        {/* Water gauge with left margin for min/max - Industrial Style */}
        <div className={`flex flex-1 min-h-0 ${isFullscreen ? 'mx-2 mb-2' : ''}`}>
          {/* Left margin - Min/Max values */}
          <div className={`flex flex-col justify-between py-1 pr-1.5 font-industrial-mono text-[#6B7280] ${isFullscreen ? 'text-sm' : 'text-[9px] md:text-xs'}`}>
            <span className="writing-mode-vertical">Max:{sensor.max_threshold}</span>
            <span className="writing-mode-vertical">Min:{sensor.min_threshold}</span>
          </div>
          
          {/* Water gauge - Industrial Style */}
          <div className={`water-gauge flex-1 relative bg-[#0A0A0A] border rounded-none ${isOffline ? 'border-red-600/50' : 'border-[#262626]'}`}>
            {/* OFFLINE Indicator Banner */}
            {isOffline && (
              <div className="absolute inset-0 z-20 flex flex-col items-center justify-center bg-black/70 backdrop-blur-sm px-2 text-center" data-testid={`gauge-incident-${sensor.name}`}>
                <div className="flex items-center gap-2 bg-red-600 px-3 py-1.5 rounded animate-pulse">
                  <WifiOff className="w-4 h-4 text-white" />
                  <span className="text-white font-bold text-sm uppercase tracking-wider">{incident?.code || 'SIN CONEXIÓN'}</span>
                </div>
                {incident?.cause && (
                  <span className="text-amber-300 text-[10px] mt-2 font-bold uppercase tracking-wide">{incident.cause}</span>
                )}
                {sensor.last_reading_time && (
                  <span className="text-zinc-500 text-[9px] mt-1 font-mono">
                    Último dato: {new Date(sensor.last_reading_time).toLocaleString('es-ES', { timeZone: 'Atlantic/Canary' })}
                  </span>
                )}
              </div>
            )}
            
            <div 
              className={`absolute bottom-0 left-0 right-0 transition-all duration-500 ${isOffline ? 'opacity-30 grayscale' : ''} ${
                sensor.status === 'normal' ? 'water-fill-blue-normal' :
                sensor.status === 'warning' ? 'water-fill-blue-warning' :
                sensor.status === 'overflow' ? 'water-fill-blue-overflow' :
                sensor.status === 'low' ? 'water-fill-blue-low' :
                'water-fill-blue-normal'
              }`}
              style={{ height: fillHeight }}
            >
              {/* Water surface line with alarm color - THICKER */}
              <div className={`absolute top-0 left-0 right-0 h-2 ${
                sensor.status === 'normal' ? 'bg-[#22c55e] shadow-[0_0_12px_rgba(34,197,94,1)]' :
                sensor.status === 'warning' ? 'bg-amber-400 shadow-[0_0_12px_rgba(251,191,36,1)]' :
                sensor.status === 'overflow' ? 'bg-red-500 shadow-[0_0_16px_rgba(239,68,68,1)] animate-pulse' :
                sensor.status === 'low' ? 'bg-blue-300 shadow-[0_0_12px_rgba(147,197,253,1)]' :
                'bg-[#22c55e] shadow-[0_0_12px_rgba(34,197,94,1)]'
              }`} />
            </div>
            
            <div className={`water-level-text ${isOffline ? 'opacity-30' : ''}`}>
              <span className={`font-bold text-white drop-shadow-[0_2px_4px_rgba(0,0,0,0.8)] ${isFullscreen ? 'text-5xl' : 'text-2xl md:text-4xl'}`}>{isOffline ? '--' : level}</span>
              <span className={`uppercase text-white/80 drop-shadow-[0_1px_2px_rgba(0,0,0,0.8)] ${isFullscreen ? 'text-sm' : 'text-[8px] md:text-xs'}`}>metros</span>
            </div>
          </div>
        </div>
      </Card>

      {/* Config Dialog */}
      <Dialog open={configOpen} onOpenChange={setConfigOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-lg max-h-[95vh] overflow-y-auto">
          <DialogHeader className="pb-2">
            <DialogTitle className="font-heading uppercase tracking-wider text-zinc-100 text-sm">
              Configurar {sensor.name}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-3 py-2">
            {/* Grid de niveles - más compacto */}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-zinc-400 text-[10px] uppercase tracking-wider">
                  Nivel Mínimo (m)
                </Label>
                <Input
                  type="text"
                  inputMode="decimal"
                  value={formatWithComma(configValues.min_threshold)}
                  onChange={handleDecimalInput('min_threshold')}
                  className="bg-zinc-800 border-zinc-700 text-zinc-100 font-mono h-8 text-sm"
                />
                <p className="text-[9px] text-blue-400">Alerta nivel bajo</p>
              </div>
              
              <div className="space-y-1">
                <Label className="text-zinc-400 text-[10px] uppercase tracking-wider">
                  Nivel Máximo (m)
                </Label>
                <Input
                  type="text"
                  inputMode="decimal"
                  value={formatWithComma(configValues.max_threshold)}
                  onChange={handleDecimalInput('max_threshold')}
                  className="bg-zinc-800 border-zinc-700 text-zinc-100 font-mono h-8 text-sm"
                />
                <p className="text-[9px] text-amber-400">Alerta nivel alto</p>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-zinc-400 text-[10px] uppercase tracking-wider">
                  Nivel de Reboso (m)
                </Label>
                <Input
                  type="text"
                  inputMode="decimal"
                  value={formatWithComma(configValues.overflow_level)}
                  onChange={handleDecimalInput('overflow_level')}
                  className="bg-zinc-800 border-zinc-700 text-zinc-100 font-mono h-8 text-sm"
                />
                <p className="text-[9px] text-red-400">Alerta desbordamiento</p>
              </div>
            
              <div className="space-y-1">
                <Label className="text-zinc-400 text-[10px] uppercase tracking-wider">
                  Margen Ajuste (m)
                </Label>
                <Input
                  type="text"
                  inputMode="decimal"
                  placeholder="Ej: -0,5"
                  value={formatWithComma(configValues.level_offset)}
                  onChange={handleDecimalInput('level_offset')}
                  className="bg-zinc-800 border-zinc-700 text-zinc-100 font-mono h-8 text-sm"
                />
                <p className="text-[9px] text-amber-400">Suma/resta a Modbus</p>
              </div>
            </div>
            
            {/* Ajuste automático de medida (calibración) */}
            <div className="border border-emerald-600/40 bg-emerald-900/10 rounded-lg p-3 mt-2">
              <Label className="text-emerald-300 text-[10px] uppercase tracking-wider mb-1 flex items-center gap-1.5">
                <Save className="w-3 h-3 text-emerald-400" />
                Ajuste de Medida (Calibración)
              </Label>
              <p className="text-[9px] text-zinc-400 mb-2">
                Lectura actual: <span className="text-cyan-400 font-mono font-bold">{sensor.current_level?.toFixed(2)} m</span>. Si hay descuadre, introduce la medida real y la app calculará el ajuste automáticamente.
              </p>
              <div className="flex gap-2">
                <Input
                  type="text"
                  inputMode="decimal"
                  placeholder="Medida real (m)"
                  value={formatWithComma(calibrateValue)}
                  onChange={(e) => { let v = e.target.value.replace('.', ','); v = v.replace(/[^0-9,-]/g, ''); const parts = v.split(','); if (parts.length > 2) v = parts[0] + ',' + parts.slice(1).join(''); setCalibrateValue(v); }}
                  className="bg-zinc-800 border-emerald-700 text-emerald-200 font-mono h-9 text-sm flex-1"
                  data-testid={`calibrate-input-${sensor.id}`}
                />
                <Button
                  onClick={handleCalibrate}
                  disabled={calibrating || !calibrateValue}
                  className="bg-emerald-600 hover:bg-emerald-700 h-9 px-4 text-sm"
                  data-testid={`calibrate-btn-${sensor.id}`}
                >
                  {calibrating ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Ajustar'}
                </Button>
              </div>

              {/* Historial de ajustes */}
              {calibrationHistory.length > 0 && (
                <div className="mt-2 pt-2 border-t border-emerald-800/40">
                  <button
                    type="button"
                    onClick={() => setShowHistory(!showHistory)}
                    className="text-[9px] uppercase tracking-wider text-zinc-400 hover:text-zinc-200 flex items-center gap-1"
                    data-testid={`calibration-history-toggle-${sensor.id}`}
                  >
                    <Clock className="w-3 h-3" />
                    Historial de ajustes ({calibrationHistory.length})
                    <span className="text-zinc-600">{showHistory ? '▼' : '▶'}</span>
                  </button>
                  {showHistory && (
                    <div className="mt-1.5 space-y-1 max-h-32 overflow-y-auto" data-testid={`calibration-history-${sensor.id}`}>
                      {calibrationHistory.map((h) => (
                        <div key={h.id} className="flex items-center justify-between bg-zinc-900/60 rounded px-2 py-1 text-[10px] font-mono">
                          <div className="flex flex-col">
                            <span className="text-zinc-300">
                              {new Date(h.created_at).toLocaleString('es-ES', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit', timeZone: 'Atlantic/Canary' })}
                            </span>
                            <span className="text-zinc-500 text-[9px]">{h.performed_by}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className={h.new_offset >= 0 ? 'text-amber-400' : 'text-blue-400'}>
                              {h.new_offset >= 0 ? '+' : ''}{h.new_offset} m
                            </span>
                            <span className="text-cyan-400">→ {h.current_level} m</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
            
            {/* Alertas de variación - más compacto */}
            <div className="border-t border-zinc-700 pt-3 mt-2">
              <Label className="text-zinc-300 text-[10px] uppercase tracking-wider mb-2 block">
                Alertas Variación (actual: {sensor.current_level?.toFixed(2)} m)
              </Label>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-zinc-400 text-[10px] uppercase">Alerta Subida (m)</Label>
                  <Input
                    type="text"
                    inputMode="decimal"
                    placeholder="Ej: 2,0"
                    value={formatWithComma(configValues.alert_rise)}
                    onChange={handleDecimalInput('alert_rise')}
                    className="bg-zinc-800 border-zinc-700 text-zinc-100 font-mono h-8 text-sm"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-zinc-400 text-[10px] uppercase">Alerta Bajada (m)</Label>
                  <Input
                    type="text"
                    inputMode="decimal"
                    placeholder="Ej: 1,5"
                    value={formatWithComma(configValues.alert_drop)}
                    onChange={handleDecimalInput('alert_drop')}
                    className="bg-zinc-800 border-zinc-700 text-zinc-100 font-mono h-8 text-sm"
                  />
                </div>
              </div>
            </div>
            
            {/* Modbus Configuration - colapsado por defecto */}
            <div className="border-t border-zinc-700 pt-2 mt-2">
              <button
                onClick={() => setShowModbus(!showModbus)}
                className="flex items-center gap-2 text-zinc-400 hover:text-zinc-200 text-[10px] uppercase tracking-wider"
              >
                <Settings className="w-3 h-3" />
                Config Modbus TCP
                <span className="text-zinc-600">{showModbus ? '▼' : '▶'}</span>
              </button>
              
              {showModbus && (
                <div className="space-y-2 bg-zinc-800/50 rounded-lg p-2 mt-2">
                  {/* Current Configuration Display (Always visible) */}
                  <div className="bg-zinc-900/80 rounded-lg p-3 border border-zinc-700">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-[10px] text-zinc-400 uppercase tracking-wider font-semibold">Configuración Actual</span>
                      {modbusLocked && (
                        <span className="flex items-center gap-1 text-[10px] text-amber-500">
                          <Lock className="w-3 h-3" />
                          BLOQUEADO
                        </span>
                      )}
                    </div>
                    <div className="grid grid-cols-2 gap-2 text-xs font-mono">
                      <div className="flex justify-between bg-zinc-800 px-2 py-1 rounded">
                        <span className="text-zinc-500">IP:</span>
                        <span className="text-cyan-400">{sensor.modbus_ip || 'No config'}</span>
                      </div>
                      <div className="flex justify-between bg-zinc-800 px-2 py-1 rounded">
                        <span className="text-zinc-500">Puerto:</span>
                        <span className="text-cyan-400">{sensor.modbus_port || '-'}</span>
                      </div>
                      <div className="flex justify-between bg-zinc-800 px-2 py-1 rounded">
                        <span className="text-zinc-500">Unit ID:</span>
                        <span className="text-cyan-400">{sensor.modbus_unit_id || '-'}</span>
                      </div>
                      <div className="flex justify-between bg-zinc-800 px-2 py-1 rounded">
                        <span className="text-zinc-500">Registro:</span>
                        <span className="text-cyan-400">{sensor.modbus_register || sensor.modbus_line || '-'}</span>
                      </div>
                      <div className="col-span-2 flex justify-between bg-zinc-800 px-2 py-1 rounded">
                        <span className="text-zinc-500">Ajuste:</span>
                        <span className={`${(sensor.level_offset || 0) !== 0 ? 'text-amber-400' : 'text-zinc-500'}`}>
                          {(sensor.level_offset || 0) > 0 ? '+' : ''}{(sensor.level_offset || 0).toFixed(2)} m
                        </span>
                      </div>
                    </div>
                  </div>
                  
                  {/* Unlock Button */}
                  {modbusLocked ? (
                    <div className="space-y-2">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          const newCount = unlockConfirmCount + 1;
                          if (newCount >= 3) {
                            setModbusLocked(false);
                            setUnlockConfirmCount(0);
                            toast.success('Configuración Modbus desbloqueada');
                          } else {
                            setUnlockConfirmCount(newCount);
                            toast.warning(`Confirmación ${newCount}/3 - Pulsa ${3 - newCount} veces más para desbloquear`);
                          }
                        }}
                        className="w-full border-amber-600/50 text-amber-500 hover:bg-amber-600/20"
                      >
                        <Lock className="w-4 h-4 mr-2" />
                        Desbloquear para Editar ({unlockConfirmCount}/3)
                      </Button>
                      <p className="text-[10px] text-zinc-500 text-center">
                        Por seguridad, pulsa 3 veces para editar la configuración Modbus
                      </p>
                    </div>
                  ) : (
                    <>
                      {/* Editable Fields */}
                      <div className="border border-emerald-600/30 rounded-lg p-3 bg-emerald-900/10">
                        <div className="flex items-center gap-2 mb-3">
                          <Unlock className="w-4 h-4 text-emerald-500" />
                          <span className="text-[10px] text-emerald-400 uppercase tracking-wider font-semibold">Modo Edición</span>
                        </div>
                        
                        <div className="grid grid-cols-2 gap-3">
                          <div className="space-y-1">
                            <Label className="text-zinc-500 text-[10px] uppercase">IP del HMI</Label>
                            <Input
                              type="text"
                              placeholder="192.168.1.100"
                              value={configValues.modbus_ip}
                              onChange={(e) => setConfigValues(prev => ({
                                ...prev,
                                modbus_ip: e.target.value
                              }))}
                              className="bg-zinc-800 border-zinc-700 text-zinc-100 font-mono text-sm"
                            />
                          </div>
                          <div className="space-y-1">
                            <Label className="text-zinc-500 text-[10px] uppercase">Puerto</Label>
                            <Input
                              type="number"
                              placeholder="502"
                              value={configValues.modbus_port}
                              onChange={(e) => setConfigValues(prev => ({
                                ...prev,
                                modbus_port: e.target.value
                              }))}
                              className="bg-zinc-800 border-zinc-700 text-zinc-100 font-mono text-sm"
                            />
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-3 mt-3">
                          <div className="space-y-1">
                            <Label className="text-zinc-500 text-[10px] uppercase">Unit ID (Slave)</Label>
                            <Input
                              type="number"
                              placeholder="247"
                              value={configValues.modbus_unit_id}
                              onChange={(e) => setConfigValues(prev => ({
                                ...prev,
                                modbus_unit_id: e.target.value
                              }))}
                              className="bg-zinc-800 border-zinc-700 text-zinc-100 font-mono text-sm"
                            />
                          </div>
                          <div className="space-y-1">
                            <Label className="text-zinc-500 text-[10px] uppercase">Registro</Label>
                            <Input
                              type="number"
                              placeholder="Ej: 10"
                              value={configValues.modbus_line}
                              onChange={(e) => setConfigValues(prev => ({
                                ...prev,
                                modbus_line: e.target.value
                              }))}
                              className="bg-zinc-800 border-zinc-700 text-zinc-100 font-mono text-sm"
                            />
                          </div>
                        </div>
                        
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setModbusLocked(true);
                            setUnlockConfirmCount(0);
                          }}
                          className="w-full mt-3 border-zinc-600 text-zinc-400 hover:bg-zinc-700"
                        >
                          <Lock className="w-4 h-4 mr-2" />
                          Volver a Bloquear
                        </Button>
                      </div>
                    </>
                  )}
                  
                  {/* Test Connection Button */}
                  <div className="border-t border-zinc-700 pt-3 mt-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={handleTestModbus}
                      disabled={testingModbus || !configValues.modbus_ip}
                      className="w-full border-zinc-600 text-zinc-300 hover:bg-zinc-700"
                    >
                      {testingModbus ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Probando...
                        </>
                      ) : (
                        <>
                          <Wifi className="w-4 h-4 mr-2" />
                          Test Conexión
                        </>
                      )}
                    </Button>
                    {modbusTestResult && (
                      <div className={`mt-2 p-2 rounded text-xs ${modbusTestResult.success ? 'bg-emerald-500/20 text-emerald-400' : 'bg-red-500/20 text-red-400'}`}>
                        {modbusTestResult.message}
                      </div>
                    )}
                  </div>
                  
                  <p className="text-[10px] text-zinc-500">
                    Configure estos valores según la documentación del HMI Weintek
                  </p>
                </div>
              )}
            </div>
          </div>
          
          <div className="flex justify-end gap-2 pt-2 border-t border-zinc-800">
            <Button 
              variant="ghost" 
              onClick={() => setConfigOpen(false)}
              className="text-zinc-400 h-8 text-sm"
            >
              Cancelar
            </Button>
            <Button 
              onClick={handleSaveConfig}
              disabled={saving}
              className="bg-blue-600 hover:bg-blue-700 text-white h-8 text-sm"
            >
              {saving ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Save className="w-4 h-4 mr-2" />
              )}
              Guardar
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

const Dashboard = ({ cameras, sensors, loading, onRefresh, user, onLogout, connectionStatus, lastSuccessfulFetch, retryCount, onReconnect }) => {
  const [fullscreenCamera, setFullscreenCamera] = useState(null);
  const [fullscreenWeather, setFullscreenWeather] = useState(false);
  const [fullscreenChart, setFullscreenChart] = useState(false);
  const [fullscreenSensors, setFullscreenSensors] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [showAlarmDetails, setShowAlarmDetails] = useState(false);
  const [proxyAutoSynced, setProxyAutoSynced] = useState(false);
  const [consumptionByDam, setConsumptionByDam] = useState({});
  const [showAdvancedStats, setShowAdvancedStats] = useState(false);
  const [showCameras, setShowCameras] = useState(true);
  const [showIrrigationStats, setShowIrrigationStats] = useState(false);
  const [showSeismicHistory, setShowSeismicHistory] = useState(false);
  const [showDailyReport, setShowDailyReport] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [damStatus, setDamStatus] = useState({});

  // Update clock every second
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  // Check dam network status every 30 seconds
  useEffect(() => {
    const checkDamStatus = async () => {
      try {
        const response = await axios.get(`${API}/dams/status`);
        setDamStatus(response.data);
      } catch (error) {
        console.error('Error checking dam status:', error);
      }
    };
    
    checkDamStatus();
    const interval = setInterval(checkDamStatus, 30000);
    return () => clearInterval(interval);
  }, []);

  // Offline sync hook for irrigation data
  const token = localStorage.getItem('token');
  const { 
    isOnline, 
    pendingCount, 
    isSyncing, 
    forceSync 
  } = useOfflineSync(API, token);

  // Fetch consumption by dam
  useEffect(() => {
    const fetchConsumption = async () => {
      try {
        const token = localStorage.getItem('token');
        if (token) {
          const response = await axios.get(`${API}/irrigation/consumption/by-dam`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          if (response.data?.by_dam) {
            setConsumptionByDam(response.data.by_dam);
          }
        }
      } catch (error) {
        console.error('Error fetching consumption by dam:', error);
      }
    };
    
    // Execute immediately
    fetchConsumption();
    // Refresh every 5 minutes
    const interval = setInterval(fetchConsumption, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  // Auto-sync from proxy when accessing from production (different domain)
  useEffect(() => {
    const autoSyncFromProxy = async () => {
      // Check if we're on a different domain than the preview (production)
      const currentHost = window.location.hostname;
      // Producción = cualquier host que no sea el preview ni desarrollo local
      // (no comparar con el host del backend: en producción pueden compartir dominio)
      const isProduction = !currentHost.includes('preview.emergentagent.com') && currentHost !== 'localhost' && currentHost !== '127.0.0.1';
      
      if (isProduction && !proxyAutoSynced) {
        console.log('Producción detectada - sincronizando desde proxy automáticamente...');
        try {
          const token = localStorage.getItem('token');
          if (token) {
            const response = await fetch(`${API}/sensors/sync-from-proxy`, {
              method: 'POST',
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
              }
            });
            
            if (response.ok) {
              const data = await response.json();
              if (data.success) {
                console.log('Sincronización automática exitosa:', data.message);
                // Removed noisy sync toast
                // toast.success('Datos sincronizados desde servidor proxy');
                onRefresh(); // Refresh the UI with new data
              }
            }
          }
        } catch (error) {
          console.error('Error en sincronización automática:', error);
        }
        setProxyAutoSynced(true);
      }
    };

    autoSyncFromProxy();
  }, [proxyAutoSynced, onRefresh]);

  // Periodic auto-sync for production (every 5 seconds)
  useEffect(() => {
    const currentHost = window.location.hostname;
    const isProduction = !currentHost.includes('preview.emergentagent.com') && currentHost !== 'localhost' && currentHost !== '127.0.0.1';
    
    if (!isProduction) return;

    const syncInterval = setInterval(async () => {
      console.log('Sincronización periódica desde proxy (5s)...');
      try {
        const token = localStorage.getItem('token');
        if (token) {
          const response = await fetch(`${API}/sensors/sync-from-proxy`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          });
          
          if (response.ok) {
            const data = await response.json();
            if (data.success) {
              console.log('Sincronización periódica exitosa');
              onRefresh();
            }
          }
        }
      } catch (error) {
        console.error('Error en sincronización periódica:', error);
      }
    }, 5 * 1000); // 5 seconds

    return () => clearInterval(syncInterval);
  }, [onRefresh]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await onRefresh();
    setTimeout(() => setRefreshing(false), 500);
    toast.success("Datos actualizados");
  };

  const overflowCount = (sensors || []).filter(s => s.status === 'overflow').length;
  const criticalCount = (sensors || []).filter(s => s.status === 'critical').length;
  const warningCount = (sensors || []).filter(s => s.status === 'warning').length;
  
  // Get camera key for fullscreen
  const getFullscreenKey = () => {
    if (!fullscreenCamera) return '';
    const name = fullscreenCamera.name.toLowerCase().replace(' ', '');
    const keyMap = {
      'parralillo': 'parralillo',
      'siberio1': 'siberio1',
      'siberio2': 'siberio2',
      'caideros1': 'caideros1',
      'caideros2': 'caideros2'
    };
    return keyMap[name] || name;
  };

  // Vista exclusiva "Visor de Sondas": solo lectura de los niveles de las sondas
  if (user?.role === 'visor') {
    return (
      <VisorView
        sensors={sensors}
        currentTime={currentTime}
        onRefresh={handleRefresh}
        onLogout={onLogout}
        refreshing={refreshing}
      />
    );
  }


  return (
    <div className="dashboard-container bg-obsidian-base pt-2 noise-bg" data-testid="dashboard-container">
      {/* Header - Command Center Style */}
      <header className="dashboard-header glass border-b border-white/10 flex items-center justify-between px-3 md:px-5 mx-2 md:mx-4" style={{ height: '60px' }}>
        {/* Left section: Logo + Title + Clock */}
        <div className="flex items-center h-full">
          <div className="flex items-center gap-3 md:gap-4 min-w-0">
            <a href="https://livewifi.es/centinela" target="_blank" rel="noopener noreferrer" className="hidden md:block shrink-0 hover:opacity-80 transition-opacity">
              <svg viewBox="0 0 2952 631" style={{ width: '110px', height: '24px' }}>
                <image href="https://customer-assets.emergentagent.com/job_65caa12a-9cef-4fb1-b65d-555171978c75/artifacts/uc0sdgit_LOGO%20FINAL%20LIVEWIFI%20BLANCO.png" width="2952" height="631" />
              </svg>
            </a>
            <div className="flex items-center">
              <h1 className="font-heading font-black text-lg md:text-xl uppercase tracking-tighter text-white">
                CENTINELA
              </h1>
              <div className="ml-2 px-2 py-0.5 bg-water-flow/10 border border-water-flow/30">
                <span className="text-water-flow text-[10px] font-mono font-bold tracking-wider">v2.0</span>
              </div>
            </div>
            {/* Clock - Canary Islands Time */}
            <div className="hidden sm:flex items-center gap-3 ml-4 pl-4 border-l border-white/10">
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4 text-water-flow/60" />
                <span className="font-mono text-sm text-white font-medium tabular-nums">
                  {currentTime.toLocaleTimeString('es-ES', { 
                    hour: '2-digit', 
                    minute: '2-digit', 
                    second: '2-digit',
                    timeZone: 'Atlantic/Canary' 
                  })}
                </span>
              </div>
              <span className="text-xs text-zinc-500 font-mono">
                {currentTime.toLocaleDateString('es-ES', { 
                  day: '2-digit', 
                  month: '2-digit',
                  timeZone: 'Atlantic/Canary' 
                })}
              </span>
            </div>
            
            {/* Dam Network Status Banner - DISABLED */}
            {/* 
            <div className="hidden md:flex items-center gap-1.5 ml-3 pl-3 border-l border-[#404040]">
              {['PARRALILLO', 'SIBERIO', 'CAIDERO'].map((dam) => {
                const status = damStatus[dam]?.status || 'checking';
                const isOnline = status === 'online';
                const isChecking = status === 'checking';
                return (
                  <div 
                    key={dam}
                    className={`flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-mono uppercase ${
                      isChecking 
                        ? 'bg-zinc-700/50 text-zinc-400' 
                        : isOnline 
                          ? 'bg-green-600/20 text-green-400 border border-green-600/30' 
                          : 'bg-red-600/20 text-red-400 border border-red-600/30 animate-pulse'
                    }`}
                    title={`${dam}: ${isOnline ? 'Conexión activa' : 'Sin conexión'}`}
                  >
                    <span className={`w-1.5 h-1.5 rounded-full ${
                      isChecking ? 'bg-zinc-500' : isOnline ? 'bg-green-400' : 'bg-red-400'
                    }`} />
                    <span>{dam.substring(0, 3)}</span>
                  </div>
                );
              })}
            </div>
            */}
          </div>
          
          {/* Status summary - Industrial Pills - Hidden on mobile */}
          <div className="hidden lg:flex items-center gap-3 ml-6 pl-6 border-l border-white/10">
              {overflowCount > 0 && (
                <div 
                  className="flex items-center gap-2 bg-status-alert/10 border border-status-alert/30 px-3 py-1.5 cursor-pointer hover:bg-status-alert/20 transition-colors group"
                  onClick={() => setShowAlarmDetails(true)}
                  data-testid="overflow-badge"
                >
                  <span className="w-2 h-2 bg-status-alert rounded-full animate-pulse shadow-[0_0_8px_rgba(255,59,48,0.8)]"></span>
                  <span className="text-status-alert text-xs font-mono font-bold tracking-wider">{overflowCount} DESBORDE</span>
                </div>
              )}
              {criticalCount > 0 && (
                <div 
                  className="flex items-center gap-2 bg-status-alert/10 border border-status-alert/30 px-3 py-1.5 cursor-pointer hover:bg-status-alert/20 transition-colors"
                  onClick={() => setShowAlarmDetails(true)}
                >
                  <span className="w-2 h-2 bg-status-alert rounded-full"></span>
                  <span className="text-status-alert text-xs font-mono font-bold tracking-wider">{criticalCount} CRITICO</span>
                </div>
              )}
              {warningCount > 0 && (
                <div 
                  className="flex items-center gap-2 bg-status-warning/10 border border-status-warning/30 px-3 py-1.5 cursor-pointer hover:bg-status-warning/20 transition-colors"
                  onClick={() => setShowAlarmDetails(true)}
                >
                  <span className="w-2 h-2 bg-status-warning rounded-full"></span>
                  <span className="text-status-warning text-xs font-mono font-bold tracking-wider">{warningCount} ALERTA</span>
                </div>
              )}
              {overflowCount === 0 && criticalCount === 0 && warningCount === 0 && (
                <div 
                  className="flex items-center gap-2 bg-status-normal/10 border border-status-normal/30 px-3 py-1.5 cursor-pointer hover:bg-status-normal/20 transition-colors"
                  onClick={() => setShowAlarmDetails(true)}
                >
                  <span className="w-2 h-2 bg-status-normal rounded-full shadow-[0_0_6px_rgba(0,230,118,0.6)]"></span>
                  <span className="text-status-normal text-xs font-mono font-bold tracking-wider">SISTEMA OK</span>
                </div>
              )}
            </div>
          </div>
          
          {/* Right section: Action buttons - Responsive */}
          <div className="flex items-center gap-1 md:gap-4">
            <TooltipProvider delayDuration={200}>
              {/* IrrigationManager - requires can_manage_irrigation - NOW VISIBLE ON MOBILE */}
              {user?.permissions?.can_manage_irrigation && (
                <IrrigationManager user={user} />
              )}
              
              {/* Irrigation Stats Button - Estadísticas de Riego - Visible para todos */}
              <Tooltip>
                <TooltipTrigger asChild>
                  <span><IrrigationStatsButton onClick={() => setShowIrrigationStats(true)} /></span>
                </TooltipTrigger>
                <TooltipContent className="bg-zinc-800 text-zinc-100 border-zinc-700">
                  <p>Estadísticas de Riego La Aldea</p>
                </TooltipContent>
              </Tooltip>
              
              {/* Seismic History Button - Histórico de Sismos */}
              <Tooltip>
                <TooltipTrigger asChild>
                  <span><SeismicHistoryButton onClick={() => setShowSeismicHistory(true)} /></span>
                </TooltipTrigger>
                <TooltipContent className="bg-zinc-800 text-zinc-100 border-zinc-700">
                  <p>Histórico de Sismos</p>
                </TooltipContent>
              </Tooltip>
              
              {/* Daily Report Button - Parte Diario de Riego */}
              <Tooltip>
                <TooltipTrigger asChild>
                  <span><DailyReportButton onClick={() => setShowDailyReport(true)} /></span>
                </TooltipTrigger>
                <TooltipContent className="bg-zinc-800 text-zinc-100 border-zinc-700">
                  <p>Parte Diario de Riego</p>
                </TooltipContent>
              </Tooltip>
              
              {/* QuickUpdate - requires can_update_levels - NOW VISIBLE ON MOBILE */}
              {user?.permissions?.can_update_levels && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span><QuickUpdate sensors={sensors} onUpdate={onRefresh} /></span>
                  </TooltipTrigger>
                  <TooltipContent className="bg-zinc-800 text-zinc-100 border-zinc-700">
                    <p>Actualizar Niveles</p>
                  </TooltipContent>
                </Tooltip>
              )}
              
              {/* AlertsPanel - visible to all */}
              <Tooltip>
                <TooltipTrigger asChild>
                  <span><AlertsPanel /></span>
                </TooltipTrigger>
                <TooltipContent className="bg-zinc-800 text-zinc-100 border-zinc-700">
                  <p>Historial de Alertas</p>
                </TooltipContent>
              </Tooltip>
              
              {/* Push Notifications Toggle - visible to all */}
              <PushNotificationToggle />
              
              {/* Install PWA Button */}
              <InstallPWA />
              
              {/* Advanced Statistics Button */}
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button 
                    variant="ghost" 
                    size="icon"
                    onClick={() => setShowAdvancedStats(true)}
                    className="text-zinc-400 hover:text-purple-400 h-8 w-8 md:h-10 md:w-10 p-0"
                    data-testid="stats-btn"
                  >
                    <BarChart3 className="w-5 h-5 md:w-6 md:h-6" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent className="bg-zinc-800 text-zinc-100 border-zinc-700">
                  <p>Estadísticas Avanzadas</p>
                </TooltipContent>
              </Tooltip>
              
              {/* Data Export Button */}
              <Tooltip>
                <TooltipTrigger asChild>
                  <span><DataExport /></span>
                </TooltipTrigger>
                <TooltipContent className="bg-zinc-800 text-zinc-100 border-zinc-700">
                  <p>Exportar Datos</p>
                </TooltipContent>
              </Tooltip>
              
              {/* Offline Indicator */}
              <OfflineIndicator 
                isOnline={isOnline}
                pendingCount={pendingCount}
                isSyncing={isSyncing}
                onSync={forceSync}
              />
              
              {/* ReportGenerator - Hidden on mobile */}
              <span className="hidden md:block">
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span><ReportGenerator /></span>
                  </TooltipTrigger>
                  <TooltipContent className="bg-zinc-800 text-zinc-100 border-zinc-700">
                    <p>Descargar Informes</p>
                  </TooltipContent>
                </Tooltip>
              </span>
              
              {/* NotificationSettings - requires can_configure_system - Hidden on mobile */}
              <span className="hidden md:block">
                {user?.permissions?.can_configure_system && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span><NotificationSettings /></span>
                    </TooltipTrigger>
                    <TooltipContent className="bg-zinc-800 text-zinc-100 border-zinc-700">
                      <p>Configurar Notificaciones</p>
                    </TooltipContent>
                  </Tooltip>
                )}
              </span>
              
              {/* EpisodeSimulator - requires can_configure_system - Hidden on mobile */}
              <span className="hidden lg:block">
                {user?.permissions?.can_configure_system && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span><EpisodeSimulator sensors={sensors} /></span>
                    </TooltipTrigger>
                    <TooltipContent className="bg-zinc-800 text-zinc-100 border-zinc-700">
                      <p>Simulador Hidrológico</p>
                    </TooltipContent>
                  </Tooltip>
                )}
              </span>
              
              {/* EventLog - Hidden on mobile */}
              <span className="hidden md:block">
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span><EventLog /></span>
                  </TooltipTrigger>
                  <TooltipContent className="bg-zinc-800 text-zinc-100 border-zinc-700">
                    <p>Registro de Eventos</p>
                  </TooltipContent>
                </Tooltip>
              </span>
              
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button 
                    variant="ghost" 
                    size="icon"
                    onClick={handleRefresh}
                    disabled={refreshing}
                    className="text-zinc-400 hover:text-zinc-100 h-8 w-8 md:h-10 md:w-10 p-0"
                    data-testid="refresh-btn"
                  >
                    <RefreshCw className={`w-8 h-8 md:w-[44px] md:h-[44px] ${refreshing ? 'animate-spin' : ''}`} />
                  </Button>
                </TooltipTrigger>
                <TooltipContent className="bg-zinc-800 text-zinc-100 border-zinc-700">
                  <p>Actualizar Datos</p>
                </TooltipContent>
              </Tooltip>
              
              {/* Settings - requires can_configure_sensors or can_configure_system */}
              {(user?.permissions?.can_configure_sensors || user?.permissions?.can_configure_system) && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Link to="/settings">
                      <Button 
                        variant="ghost" 
                        size="icon"
                        className="text-zinc-400 hover:text-zinc-100 h-8 w-8 md:h-10 md:w-10 p-0"
                        data-testid="settings-btn"
                      >
                        <Settings className="w-8 h-8 md:w-[44px] md:h-[44px]" />
                      </Button>
                    </Link>
                  </TooltipTrigger>
                  <TooltipContent className="bg-zinc-800 text-zinc-100 border-zinc-700">
                    <p>Configuración</p>
                  </TooltipContent>
                </Tooltip>
              )}
              
              {/* User Management - requires can_manage_users - Hidden on mobile */}
              <span className="hidden md:block">
                {user?.permissions?.can_manage_users && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Link to="/users">
                        <Button 
                          variant="ghost" 
                          size="icon"
                          className="text-zinc-400 hover:text-zinc-100 h-10 w-10 p-0"
                          data-testid="users-btn"
                        >
                          <Users className="w-[44px] h-[44px]" />
                        </Button>
                      </Link>
                    </TooltipTrigger>
                    <TooltipContent className="bg-zinc-800 text-zinc-100 border-zinc-700">
                      <p>Gestión de Usuarios</p>
                    </TooltipContent>
                  </Tooltip>
                )}
              </span>
              
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button 
                    variant="ghost" 
                    size="icon"
                    onClick={onLogout}
                    className="text-zinc-400 hover:text-red-400 h-8 w-8 md:h-10 md:w-10 p-0"
                    data-testid="logout-btn"
                  >
                    <LogOut className="w-8 h-8 md:w-[44px] md:h-[44px]" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent className="bg-zinc-800 text-zinc-100 border-zinc-700">
                  <p>Cerrar Sesión</p>
                </TooltipContent>
              </Tooltip>
              
              {/* Connection Status Indicator */}
              {connectionStatus && (
                <ConnectionIndicator 
                  status={connectionStatus}
                  lastUpdate={lastSuccessfulFetch}
                  retryCount={retryCount}
                  onReconnect={onReconnect}
                />
              )}
            </TooltipProvider>
          </div>
      </header>

      {/* Main Content */}
      <main className="dashboard-main" style={{ margin: 0, padding: 0 }}>
        
        {/* MOBILE LAYOUT: Stack vertically - optimized for all orientations */}
        <div className="mobile-layout md:hidden h-full overflow-y-auto" style={{ maxHeight: 'calc(100vh - 50px)' }}>
          <div className="space-y-2 px-2 pb-2">
            {/* Niveles de Agua - PRIORIDAD - 3 columnas más grandes */}
            <div className="grid grid-cols-3 gap-2">
              {sensors.find(s => s.name === "PARRALILLO") && (
                <div style={{ height: '140px' }}>
                  <WaterGauge sensor={sensors.find(s => s.name === "PARRALILLO")} onUpdate={onRefresh} onClick={() => setFullscreenSensors(true)} user={user} consumption={consumptionByDam.PARRALILLO} />
                </div>
              )}
              {sensors.find(s => s.name === "SIBERIO") && (
                <div style={{ height: '140px' }}>
                  <WaterGauge sensor={sensors.find(s => s.name === "SIBERIO")} onUpdate={onRefresh} onClick={() => setFullscreenSensors(true)} user={user} consumption={consumptionByDam.SIBERIO} />
                </div>
              )}
              {sensors.find(s => s.name === "CAIDERO") && (
                <div style={{ height: '140px' }}>
                  <WaterGauge sensor={sensors.find(s => s.name === "CAIDERO")} onUpdate={onRefresh} onClick={() => setFullscreenSensors(true)} user={user} consumption={consumptionByDam.CAIDERO} />
                </div>
              )}
            </div>
            
            {/* Widget Tiempo y Gráfica */}
            <div className="grid grid-cols-2 gap-2">
              <div style={{ minHeight: '180px' }}><WeatherWidget vertical={true} prioritizeEstimates={true} /></div>
              <div style={{ minHeight: '180px' }}><LevelsChart sensors={sensors} /></div>
            </div>
            
            {/* Cámaras - Grid compacto - Solo si tiene permiso */}
            {user?.permissions?.can_view_cameras && (
              <div className="grid grid-cols-2 gap-1.5">
                {cameras.find(c => c.name === "PARRALILLO") && (
                  <div className="col-span-2" style={{ height: '80px' }}>
                    <CameraFeed camera={cameras.find(c => c.name === "PARRALILLO")} onFullscreen={setFullscreenCamera} delayIndex={0} />
                  </div>
                )}
                {cameras.find(c => c.name === "SIBERIO 1") && (
                  <div style={{ height: '65px' }}><CameraFeed camera={cameras.find(c => c.name === "SIBERIO 1")} onFullscreen={setFullscreenCamera} delayIndex={1} /></div>
                )}
                {cameras.find(c => c.name === "SIBERIO 2") && (
                  <div style={{ height: '65px' }}><CameraFeed camera={cameras.find(c => c.name === "SIBERIO 2")} onFullscreen={setFullscreenCamera} delayIndex={2} /></div>
                )}
                {cameras.find(c => c.name === "CAIDERO 1") && (
                  <div style={{ height: '65px' }}><CameraFeed camera={cameras.find(c => c.name === "CAIDERO 1")} onFullscreen={setFullscreenCamera} delayIndex={3} /></div>
                )}
                {cameras.find(c => c.name === "CAIDERO 2") && (
                  <div style={{ height: '65px' }}><CameraFeed camera={cameras.find(c => c.name === "CAIDERO 2")} onFullscreen={setFullscreenCamera} delayIndex={4} /></div>
                )}
              </div>
            )}
            
            {/* Panel de Alertas y Estadísticas */}
            <div style={{ minHeight: '220px' }}>
              <AlertsStatsPanel sensors={sensors} />
            </div>
            
            {/* Widget de Riego - Estadísticas de La Aldea */}
            <IrrigationWidget onClick={() => setShowIrrigationStats(true)} />
          </div>
        </div>
        
        {/* DESKTOP LAYOUT: 4 columns x 3 rows = 12 squares */}
        <div className="desktop-grid">
          {/* ROW 1 */}
          {/* Chart - LEFT */}
          <div 
            className="relative group cursor-pointer bg-obsidian-surface border border-white/5"
            onDoubleClick={() => setFullscreenChart(true)}
          >
            <LevelsChart sensors={sensors} compact={true} />
            <div className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 transition-opacity z-10">
              <Button 
                size="icon" 
                variant="ghost" 
                className="h-5 w-5 bg-black/50 hover:bg-black/70 text-white"
                onClick={() => setFullscreenChart(true)}
              >
                <Maximize2 className="w-3 h-3" />
              </Button>
            </div>
          </div>
          
          {/* PARRALILLO Camera - 2 columns - Solo si tiene permiso y showCameras */}
          {user?.permissions?.can_view_cameras && showCameras ? (
            <div className="col-span-2 bg-obsidian-surface border border-white/5">
              {cameras.find(c => c.name === "PARRALILLO") && (
                <CameraFeed 
                  camera={cameras.find(c => c.name === "PARRALILLO")}
                  onFullscreen={setFullscreenCamera}
                />
              )}
            </div>
          ) : (
            <div className="col-span-2 bg-obsidian-surface border border-white/5 flex items-center justify-center">
              <div className="text-center p-4">
                {!showCameras ? (
                  <>
                    <Camera className="w-12 h-12 text-zinc-600 mx-auto mb-2" />
                    <p className="text-xs text-zinc-500">Cámaras Ocultas</p>
                    <button 
                      onClick={() => setShowCameras(true)}
                      className="text-[10px] text-blue-400 hover:text-blue-300 underline mt-1"
                    >
                      Mostrar cámaras
                    </button>
                  </>
                ) : (
                  <>
                    <Activity className="w-12 h-12 text-blue-500 mx-auto mb-2" />
                    <p className="text-xs text-zinc-500">Panel de Operador</p>
                    <p className="text-[10px] text-zinc-600">Solo sondas y vaciado</p>
                  </>
                )}
              </div>
            </div>
          )}
          
          {/* PARRALILLO Level */}
          <div className="bg-obsidian-surface border border-white/5">
            {sensors.find(s => s.name === "PARRALILLO") && (
              <WaterGauge sensor={sensors.find(s => s.name === "PARRALILLO")} onUpdate={onRefresh} onClick={() => setFullscreenSensors(true)} user={user} consumption={consumptionByDam.PARRALILLO} />
            )}
          </div>
          
          {/* ROW 2 */}
          {/* Weather Widget - LEFT */}
          <div 
            className="relative group cursor-pointer bg-obsidian-surface border border-white/5"
            onDoubleClick={() => setFullscreenWeather(true)}
          >
            <WeatherWidget vertical={true} prioritizeEstimates={true} />
            <div className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 transition-opacity z-10">
              <Button 
                size="icon" 
                variant="ghost" 
                className="h-5 w-5 bg-black/50 hover:bg-black/70 text-white"
                onClick={() => setFullscreenWeather(true)}
              >
                <Maximize2 className="w-3 h-3" />
              </Button>
            </div>
          </div>
          
          {/* SIBERIO 1 Camera */}
          {user?.permissions?.can_view_cameras && showCameras ? (
            <div className="bg-obsidian-surface border border-white/5">
              {cameras.find(c => c.name === "SIBERIO 1") && (
                <CameraFeed 
                  camera={cameras.find(c => c.name === "SIBERIO 1")}
                  onFullscreen={setFullscreenCamera}
                />
              )}
            </div>
          ) : (
            <div className="bg-obsidian-surface border border-white/5"></div>
          )}
          
          {/* SIBERIO 2 Camera */}
          {user?.permissions?.can_view_cameras && showCameras ? (
            <div className="bg-obsidian-surface border border-white/5">
              {cameras.find(c => c.name === "SIBERIO 2") && (
                <CameraFeed 
                  camera={cameras.find(c => c.name === "SIBERIO 2")}
                  onFullscreen={setFullscreenCamera}
                />
              )}
            </div>
          ) : (
            <div className="bg-obsidian-surface border border-white/5"></div>
          )}
          
          {/* SIBERIO Level */}
          <div className="bg-obsidian-surface border border-white/5">
            {sensors.find(s => s.name === "SIBERIO") && (
              <WaterGauge sensor={sensors.find(s => s.name === "SIBERIO")} onUpdate={onRefresh} onClick={() => setFullscreenSensors(true)} user={user} consumption={consumptionByDam.SIBERIO} />
            )}
          </div>
          
          {/* ROW 3 */}
          {/* Alerts & Stats Panel - LEFT (replaces Map) */}
          <div className="bg-obsidian-surface border border-white/5">
            <AlertsStatsPanel sensors={sensors} />
          </div>
          
          {/* CAIDERO 1 Camera */}
          {user?.permissions?.can_view_cameras && showCameras ? (
            <div className="bg-obsidian-surface border border-white/5">
              {cameras.find(c => c.name === "CAIDERO 1") && (
                <CameraFeed 
                  camera={cameras.find(c => c.name === "CAIDERO 1")}
                  onFullscreen={setFullscreenCamera}
                />
              )}
            </div>
          ) : (
            <div className="bg-obsidian-surface border border-white/5"></div>
          )}
          
          {/* CAIDERO 2 Camera */}
          {user?.permissions?.can_view_cameras && showCameras ? (
            <div className="bg-obsidian-surface border border-white/5">
              {cameras.find(c => c.name === "CAIDERO 2") && (
                <CameraFeed 
                  camera={cameras.find(c => c.name === "CAIDERO 2")}
                  onFullscreen={setFullscreenCamera}
                />
              )}
            </div>
          ) : (
            <div className="bg-obsidian-surface border border-white/5"></div>
          )}
          
          {/* CAIDERO Level */}
          <div className="bg-obsidian-surface border border-white/5">
            {sensors.find(s => s.name === "CAIDERO") && (
              <WaterGauge sensor={sensors.find(s => s.name === "CAIDERO")} onUpdate={onRefresh} onClick={() => setFullscreenSensors(true)} user={user} consumption={consumptionByDam.CAIDERO} />
            )}
          </div>
        </div>
      </main>

      {/* Fullscreen Camera Dialog */}
      <Dialog open={!!fullscreenCamera} onOpenChange={() => setFullscreenCamera(null)}>
        <DialogContent 
          className="max-w-[95vw] max-h-[95vh] w-full h-full bg-zinc-900 border-zinc-800 p-2 data-[state=open]:animate-none data-[state=closed]:animate-none"
          id="fullscreen-dialog"
          style={{ animationDuration: '0ms' }}
        >
          <DialogHeader className="flex flex-row items-center justify-between pr-8">
            <DialogTitle className="font-heading uppercase tracking-wider">
              {fullscreenCamera?.name}
            </DialogTitle>
            <Button
              variant="outline"
              size="sm"
              className="text-zinc-400 hover:text-white border-zinc-700 hover:border-zinc-500"
              onClick={() => {
                const elem = document.getElementById('fullscreen-dialog');
                if (elem) {
                  if (document.fullscreenElement) {
                    document.exitFullscreen();
                  } else {
                    elem.requestFullscreen();
                  }
                }
              }}
            >
              <Maximize2 className="w-4 h-4 mr-2" />
              Pantalla completa
            </Button>
          </DialogHeader>
          <div className="flex-1 bg-zinc-950 rounded-sm overflow-hidden" style={{ height: 'calc(95vh - 60px)' }}>
            {fullscreenCamera ? (
              <FullscreenVideoStream cameraKey={getFullscreenKey()} className="w-full h-full" />
            ) : (
              <div className="flex items-center justify-center h-full text-zinc-500">
                <Camera className="w-16 h-16" />
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Fullscreen Weather Dialog */}
      <Dialog open={fullscreenWeather} onOpenChange={setFullscreenWeather}>
        <DialogContent 
          className="max-w-[95vw] max-h-[95vh] w-full bg-zinc-900 border-zinc-800 p-4"
          id="fullscreen-weather-dialog"
        >
          <DialogHeader className="flex flex-row items-center justify-between pr-8">
            <DialogTitle className="font-heading uppercase tracking-wider text-zinc-100">
              Previsión Meteorológica AEMET
            </DialogTitle>
            <Button
              variant="outline"
              size="sm"
              className="text-zinc-400 hover:text-white border-zinc-700 hover:border-zinc-500"
              onClick={() => {
                const elem = document.getElementById('fullscreen-weather-dialog');
                if (elem) {
                  if (document.fullscreenElement) {
                    document.exitFullscreen();
                  } else {
                    elem.requestFullscreen();
                  }
                }
              }}
            >
              <Maximize2 className="w-4 h-4 mr-2" />
              Pantalla completa
            </Button>
          </DialogHeader>
          <div className="flex-1 overflow-auto" style={{ height: 'calc(95vh - 80px)' }}>
            <WeatherWidget fullscreen={true} />
          </div>
        </DialogContent>
      </Dialog>

      {/* Fullscreen Chart Dialog */}
      <Dialog open={fullscreenChart} onOpenChange={setFullscreenChart}>
        <DialogContent 
          className="max-w-[95vw] max-h-[95vh] w-full bg-zinc-900 border-zinc-800 p-4"
          id="fullscreen-chart-dialog"
        >
          <DialogHeader className="flex flex-row items-center justify-between pr-8">
            <DialogTitle className="font-heading uppercase tracking-wider text-zinc-100">
              Histórico de Niveles
            </DialogTitle>
            <Button
              variant="outline"
              size="sm"
              className="text-zinc-400 hover:text-white border-zinc-700 hover:border-zinc-500"
              onClick={() => {
                const elem = document.getElementById('fullscreen-chart-dialog');
                if (elem) {
                  if (document.fullscreenElement) {
                    document.exitFullscreen();
                  } else {
                    elem.requestFullscreen();
                  }
                }
              }}
            >
              <Maximize2 className="w-4 h-4 mr-2" />
              Pantalla completa
            </Button>
          </DialogHeader>
          <div className="flex-1" style={{ height: 'calc(95vh - 80px)' }}>
            <LevelsChart sensors={sensors} fullscreen={true} />
          </div>
        </DialogContent>
      </Dialog>

      {/* Fullscreen 3 Sensors Dialog */}
      <Dialog open={fullscreenSensors} onOpenChange={setFullscreenSensors}>
        <DialogContent 
          className="max-w-[95vw] max-h-[95vh] w-full bg-zinc-900 border-zinc-800 p-6"
          id="fullscreen-sensors-dialog"
        >
          <DialogHeader className="flex flex-row items-center justify-between pr-8 mb-4">
            <DialogTitle className="font-heading uppercase tracking-wider text-zinc-100 flex items-center gap-2">
              <Activity className="w-5 h-5 text-blue-400" />
              Niveles de las 3 Presas
            </DialogTitle>
            <Button
              variant="outline"
              size="sm"
              className="text-zinc-400 hover:text-white border-zinc-700 hover:border-zinc-500"
              onClick={() => {
                const elem = document.getElementById('fullscreen-sensors-dialog');
                if (elem) {
                  if (document.fullscreenElement) {
                    document.exitFullscreen();
                  } else {
                    elem.requestFullscreen();
                  }
                }
              }}
            >
              <Maximize2 className="w-4 h-4 mr-2" />
              Pantalla completa
            </Button>
          </DialogHeader>
          <div className="grid grid-cols-3 gap-6" style={{ height: 'calc(95vh - 120px)' }}>
            {sensors.map((sensor) => (
              <div key={sensor.id} className="h-full">
                <WaterGauge sensor={sensor} onUpdate={onRefresh} isFullscreen={true} user={user} consumption={consumptionByDam[sensor.name]} />
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Alarm Details Modal */}
      <Dialog open={showAlarmDetails} onOpenChange={setShowAlarmDetails}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-zinc-100 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-400" />
              Estado de Alarmas - Niveles
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {/* Summary */}
            <div className="grid grid-cols-4 gap-2 text-center">
              <div className="bg-zinc-800/50 rounded-lg p-3">
                <div className="text-2xl font-bold text-amber-400">{overflowCount}</div>
                <div className="text-[10px] text-zinc-500 uppercase">Desborde</div>
              </div>
              <div className="bg-zinc-800/50 rounded-lg p-3">
                <div className="text-2xl font-bold text-red-400">{criticalCount}</div>
                <div className="text-[10px] text-zinc-500 uppercase">Crítico</div>
              </div>
              <div className="bg-zinc-800/50 rounded-lg p-3">
                <div className="text-2xl font-bold text-amber-400">{warningCount}</div>
                <div className="text-[10px] text-zinc-500 uppercase">Alerta</div>
              </div>
              <div className="bg-zinc-800/50 rounded-lg p-3">
                <div className="text-2xl font-bold text-emerald-400">{sensors.filter(s => s.status === 'normal').length}</div>
                <div className="text-[10px] text-zinc-500 uppercase">Normal</div>
              </div>
            </div>

            {/* Sensor Details */}
            <div className="space-y-2">
              <h4 className="text-xs text-zinc-400 uppercase tracking-wider">Detalle por Sensor</h4>
              {sensors.map((sensor) => (
                <div 
                  key={sensor.id}
                  className={`rounded-lg p-3 border ${
                    sensor.status === 'overflow' ? 'bg-amber-500/10 border-amber-500/30' :
                    sensor.status === 'critical' ? 'bg-red-500/10 border-red-500/30' :
                    sensor.status === 'warning' ? 'bg-amber-500/10 border-amber-500/30' :
                    'bg-emerald-500/10 border-emerald-500/30'
                  }`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-semibold text-zinc-200">{sensor.name}</span>
                    <span className={`text-xs font-mono px-2 py-0.5 rounded ${
                      sensor.status === 'overflow' ? 'bg-amber-500/20 text-amber-400' :
                      sensor.status === 'critical' ? 'bg-red-500/20 text-red-400' :
                      sensor.status === 'warning' ? 'bg-amber-500/20 text-amber-400' :
                      'bg-emerald-500/20 text-emerald-400'
                    }`}>
                      {sensor.status === 'normal' ? 'OK' : sensor.status === 'overflow' ? 'DESBORDE' : sensor.status.toUpperCase()}
                    </span>
                  </div>
                  <div className="grid grid-cols-4 gap-2 text-xs">
                    <div>
                      <span className="text-zinc-500">Nivel:</span>
                      <span className="text-zinc-200 ml-1 font-mono">{sensor.level?.toFixed(2) || '0.00'}m</span>
                    </div>
                    <div>
                      <span className="text-zinc-500">Mín:</span>
                      <span className="text-blue-400 ml-1 font-mono">{sensor.min_threshold}m</span>
                    </div>
                    <div>
                      <span className="text-zinc-500">Máx:</span>
                      <span className="text-amber-400 ml-1 font-mono">{sensor.max_threshold}m</span>
                    </div>
                    <div>
                      <span className="text-zinc-500">Desborde:</span>
                      <span className="text-red-400 ml-1 font-mono">{sensor.overflow_level}m</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Legend */}
            <div className="flex items-center gap-4 text-[10px] text-zinc-500 pt-2 border-t border-zinc-800">
              <div className="flex items-center gap-1">
                <div className="w-2 h-2 rounded-full bg-emerald-500"></div>
                <span>Normal: Nivel dentro de rango</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-2 h-2 rounded-full bg-amber-500"></div>
                <span>Desborde: Nivel &gt; máximo</span>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Advanced Statistics Modal */}
      <AdvancedStatistics 
        sensors={sensors}
        isOpen={showAdvancedStats}
        onClose={() => setShowAdvancedStats(false)}
      />

      {/* Irrigation Stats Modal */}
      <IrrigationStats 
        isOpen={showIrrigationStats}
        onClose={() => setShowIrrigationStats(false)}
      />

      {/* Seismic History Modal */}
      <SeismicHistory 
        isOpen={showSeismicHistory}
        onClose={() => setShowSeismicHistory(false)}
      />

      {/* Daily Irrigation Report - auto-opens once each morning */}
      <DailyReport open={showDailyReport} onOpenChange={setShowDailyReport} />

      {/* Banner eliminado */}
    </div>
  );
};

export default Dashboard;
